// Phase SIKA-5b/2 — Goal selector tabs.
//
// Per video frames 001/003: goal pills on left, MODE chip separated by a thin
// divider, Edit goal as a link (underlined small text) on the far right.
// URL state via useSearchParams('goal'). Composes Pill primitives only.
// No primitive edits.

import { useSearchParams } from "react-router-dom";
import { Pill } from "../super-acca";

export interface GoalSelectorTabGoal {
  id: string;
  title: string;
  mode: "simple" | "chain";
  linked_chain_id: string | null;
}

export interface GoalSelectorTabsProps {
  goals: GoalSelectorTabGoal[];
  selectedGoalId: string | null;
  onSelect: (goalId: string) => void;
  onEditGoal?: () => void;
}

export function GoalSelectorTabs({
  goals,
  selectedGoalId,
  onSelect,
  onEditGoal,
}: GoalSelectorTabsProps): JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams();

  if (goals.length === 0) return null;

  const effectiveId = selectedGoalId ?? searchParams.get("goal") ?? goals[0]!.id;
  const activeGoal = goals.find((g) => g.id === effectiveId) ?? goals[0]!;
  const isChain = activeGoal.mode === "chain" && activeGoal.linked_chain_id !== null;
  const modeLabel = isChain ? "CHAIN MODE · ROLLOVER" : "SIMPLE MODE";

  const handle = (id: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set("goal", id);
    setSearchParams(next, { replace: true });
    onSelect(id);
  };

  return (
    <div
      data-testid="sika-goal-selector-tabs"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      {goals.map((g) => {
        const active = g.id === effectiveId;
        return (
          <button
            key={g.id}
            type="button"
            onClick={() => handle(g.id)}
            data-goal-id={g.id}
            data-active={active}
            style={{
              padding: "6px 12px",
              background: active ? "rgba(255,200,64,0.06)" : "transparent",
              color: active ? "var(--sa-fg-primary)" : "var(--sa-fg-muted)",
              border: active
                ? "1px solid var(--sa-accent-saffron)"
                : "1px solid transparent",
              borderRadius: 3,
              fontFamily: "var(--sa-font-stat)",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: "pointer",
              opacity: active ? 1 : 0.78,
            }}
          >
            {g.title}
          </button>
        );
      })}

      {/* MODE chip — visually separated from goal pills via a small divider.
          Always rendered (Simple Mode or Chain Mode) to make the active mode
          unambiguous. */}
      <span
        aria-hidden
        data-testid="sika-mode-divider"
        style={{
          display: "inline-block",
          width: 1,
          height: 18,
          background: "var(--sa-hairline)",
          margin: "0 2px",
        }}
      />
      <span data-testid="sika-mode-chip" data-mode={isChain ? "chain" : "simple"}>
        <Pill
          borderColor="var(--sa-accent-saffron)"
          color="var(--sa-accent-saffron)"
          background={isChain ? "rgba(255,200,64,0.06)" : "transparent"}
        >
          {modeLabel}
        </Pill>
      </span>

      {onEditGoal ? (
        <a
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            onEditGoal();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onEditGoal();
            }
          }}
          data-testid="sika-edit-goal-link"
          style={{
            marginLeft: 4,
            padding: "2px 0",
            background: "transparent",
            color: "var(--sa-fg-secondary)",
            border: "none",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11.5,
            letterSpacing: "0.04em",
            textTransform: "none",
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          Edit goal
        </a>
      ) : null}
    </div>
  );
}
